/* eslint-disable @typescript-eslint/no-use-before-define */
import { useCallback, useState } from 'react';

import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  CloudUpload,
  Download,
  FolderKanban,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react';
import * as XLSX from 'xlsx';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

import { useSyncPropertiesMutation } from '../../api/properties';
import type { Project } from '../../model/project';

type Step = 'upload' | 'confirm';
type UploadAction = 'replace' | 'add';
type StepStatus = 'done' | 'active' | 'pending';

const TEMPLATE_URL = 'https://storage.googleapis.com/lidz-static-assets/TEMPLATE/TEMPLATE_LIDZ.xlsx';
const MAX_SIZE_MB = 50;

interface ValidationError {
  row: number;
  errors: string[];
}

interface ParsedPreview {
  rowCount: number;
  rows: Record<string, unknown>[];
  headers: string[];
}

interface ErrorResponse {
  status?: number;
  data?: {
    message?: string;
    errorType?: string;
    errors?: ValidationError[];
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  masterProject: Pick<Project, 'id' | 'name'>;
  subproject: Pick<Project, 'id' | 'name'>;
}

const parseExcel = async (file: File): Promise<ParsedPreview> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  return { rows, headers, rowCount: rows.length };
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getStepClassName = (status: StepStatus): string => {
  if (status === 'done') {
    return 'flex size-6 items-center justify-center rounded-full bg-brand-vivid text-text-inverse';
  }
  if (status === 'active') {
    return 'flex size-6 items-center justify-center rounded-full border-2 border-brand-vivid bg-bg-surface text-brand-vivid font-semibold text-xs';
  }
  return 'flex size-6 items-center justify-center rounded-full border-2 border-border-subtle bg-bg-surface text-text-subtle font-semibold text-xs';
};

const PropertyUploadDialog = ({ open, onOpenChange, masterProject, subproject }: Props) => {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ParsedPreview | null>(null);
  const [action, setAction] = useState<UploadAction>('add');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  const [syncProperties, { isLoading: isSyncing }] = useSyncPropertiesMutation();

  const reset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setAction('add');
    setIsDragging(false);
    setParseError(null);
    setValidationErrors([]);
    setServerError(null);
  }, []);

  const handleClose = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFile = useCallback(async (selected: File) => {
    setParseError(null);
    setValidationErrors([]);
    setServerError(null);

    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      setParseError(`El archivo supera el tamaño máximo de ${MAX_SIZE_MB} MB.`);
      return;
    }
    if (!/\.(xls|xlsx|csv)$/i.test(selected.name)) {
      setParseError('Formato no soportado. Usa archivos .xlsx, .xls o .csv.');
      return;
    }
    setFile(selected);
    try {
      const parsed = await parseExcel(selected);
      setPreview(parsed);
    } catch (error) {
      setParseError(`No se pudo leer el archivo: ${error instanceof Error ? error.message : 'error desconocido'}`);
    }
  }, []);

  const handleConfirm = async (): Promise<void> => {
    if (!file) return;
    setServerError(null);
    setValidationErrors([]);
    try {
      await syncProperties({
        projectId: masterProject.id,
        subprojectId: subproject.id,
        propertiesFile: file,
        action,
      }).unwrap();
      handleClose(false);
    } catch (err) {
      const error = err as ErrorResponse;
      const { data } = error;
      if (data?.errorType === 'validation_error' && Array.isArray(data.errors)) {
        setValidationErrors(data.errors);
        setStep('upload');
      }
      setServerError(data?.message ?? 'Ocurrió un error al procesar el archivo.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!max-w-4xl !p-0 gap-0 overflow-hidden sm:!max-w-4xl w-[calc(100%-2rem)]">
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] min-h-[520px]">
          {/* Left rail */}
          <aside className="bg-bg-surface-raised p-6 flex flex-col gap-6 border-r border-border-subtle rounded-l-xl">
            <DialogHeader className="text-left space-y-1">
              <DialogTitle className="font-display text-brand-navy text-xl leading-tight">
                Carga Masiva de Propiedades
              </DialogTitle>
              <DialogDescription className="text-xs">Sube tu archivo para actualizar el inventario.</DialogDescription>
            </DialogHeader>

            <ol className="flex flex-col gap-4 mt-2">
              <StepIndicator status="done" index={1} label="Seleccionar Proyecto" />
              <StepIndicator status={step === 'upload' ? 'active' : 'done'} index={2} label="Cargar Archivo" />
              <StepIndicator status={step === 'confirm' ? 'active' : 'pending'} index={3} label="Revisar y Confirmar" />
            </ol>

            <div className="mt-auto rounded-lg border border-border-subtle bg-bg-surface p-4">
              <p className="text-xs font-semibold text-brand-navy mb-1">Plantilla de Carga</p>
              <p className="text-[11px] text-text-subtle mb-3">
                Descarga el formato base para asegurar una carga exitosa.
              </p>
              <a href={TEMPLATE_URL} target="_blank" rel="noreferrer" className="w-full">
                <Button variant="outline" size="sm" className="w-full">
                  <Download className="size-3.5" />
                  Descargar plantilla
                </Button>
              </a>
            </div>
          </aside>

          {/* Right content */}
          <div className="flex flex-col p-6 pr-12 gap-4 overflow-y-auto max-h-[70vh] bg-bg-surface">
            {step === 'upload' ? (
              <UploadStep
                file={file}
                preview={preview}
                isDragging={isDragging}
                onDragChange={setIsDragging}
                onFile={(f) => {
                  handleFile(f).catch(() => {});
                }}
                onClear={() => {
                  setFile(null);
                  setPreview(null);
                  setParseError(null);
                  setValidationErrors([]);
                }}
                parseError={parseError}
                validationErrors={validationErrors}
                serverError={validationErrors.length === 0 ? serverError : null}
              />
            ) : (
              <ConfirmStep
                masterProject={masterProject}
                subproject={subproject}
                propertyCount={preview?.rowCount ?? 0}
                action={action}
                onActionChange={setAction}
                serverError={serverError}
              />
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-border-subtle px-6 py-4 sm:justify-between bg-bg-surface rounded-b-xl">
          <DialogClose asChild className="pb-2">
            <Button variant="ghost" type="button">
              Cancelar
            </Button>
          </DialogClose>
          {step === 'upload' ? (
            <div className="flex items-center gap-2 pb-2">
              {validationErrors.length > 0 && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                    setParseError(null);
                    setValidationErrors([]);
                    setServerError(null);
                  }}
                >
                  Atrás
                </Button>
              )}
              <Button
                type="button"
                onClick={() => setStep('confirm')}
                disabled={!file || !preview || validationErrors.length > 0 || parseError !== null}
              >
                Siguiente
                <ArrowRight className="size-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 pb-2">
              <Button variant="outline" type="button" onClick={() => setStep('upload')} disabled={isSyncing}>
                Atrás
              </Button>
              <Button
                type="button"
                onClick={() => {
                  handleConfirm().catch(() => {});
                }}
                disabled={isSyncing}
              >
                {isSyncing ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
                {isSyncing ? 'Procesando…' : 'Confirmar y procesar'}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface StepIndicatorProps {
  status: StepStatus;
  index: number;
  label: string;
}

const StepIndicator = ({ status, index, label }: StepIndicatorProps) => (
  <li className="flex items-center gap-3">
    <span className={getStepClassName(status)}>{status === 'done' ? <CheckCircle2 className="size-4" /> : index}</span>
    <span className={status === 'pending' ? 'text-sm text-text-subtle' : 'text-sm font-semibold text-brand-navy'}>
      {label}
    </span>
  </li>
);

interface UploadStepProps {
  file: File | null;
  preview: ParsedPreview | null;
  isDragging: boolean;
  onDragChange: (dragging: boolean) => void;
  onFile: (file: File) => void;
  onClear: () => void;
  parseError: string | null;
  validationErrors: ValidationError[];
  serverError: string | null;
}

const UploadStep = ({
  file,
  preview,
  isDragging,
  onDragChange,
  onFile,
  onClear,
  parseError,
  validationErrors,
  serverError,
}: UploadStepProps) => (
  <div className="flex flex-col gap-4">
    {!file ? (
      <label
        htmlFor="properties-file-input"
        onDrop={(e) => {
          e.preventDefault();
          onDragChange(false);
          const dropped = e.dataTransfer.files?.[0];
          if (dropped) onFile(dropped);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          onDragChange(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          onDragChange(false);
        }}
        className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 cursor-pointer transition ${
          isDragging
            ? 'border-brand-vivid bg-brand-light/40'
            : 'border-border-default bg-bg-surface-raised hover:border-brand-vivid hover:bg-brand-light/30'
        }`}
      >
        <span className="flex size-14 items-center justify-center rounded-full bg-brand-vivid text-text-inverse">
          <CloudUpload className="size-7" />
        </span>
        <p className="text-base font-semibold text-brand-navy">Arrastra tu archivo aquí o haz clic para subir</p>
        <p className="text-xs text-text-subtle">
          Formatos soportados: .xlsx o .csv · Tamaño máximo
          {MAX_SIZE_MB} MB.
        </p>
        <input
          id="properties-file-input"
          type="file"
          accept=".xls,.xlsx,.csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />
      </label>
    ) : (
      <div className="flex items-center justify-between rounded-xl border border-border-subtle bg-bg-surface-raised p-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex size-10 items-center justify-center rounded-full bg-brand-light text-brand-vivid">
            <CloudUpload className="size-5" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-navy truncate">{file.name}</p>
            <p className="text-xs text-text-subtle">
              {formatSize(file.size)} ·{preview?.rowCount ?? 0} filas
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClear}>
          <Trash2 className="size-4" />
          Quitar
        </Button>
      </div>
    )}

    {parseError && (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>No se pudo procesar el archivo</AlertTitle>
        <AlertDescription>{parseError}</AlertDescription>
      </Alert>
    )}

    {validationErrors.length > 0 && (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Errores encontrados en el archivo</AlertTitle>
        <AlertDescription>
          <p>
            Se encontraron errores de validación en
            {validationErrors.length} fila(s):
          </p>
          <ul className="mt-2 max-h-40 overflow-y-auto list-disc pl-5 text-xs space-y-1">
            {validationErrors.slice(0, 25).map((err) => (
              <li key={err.row}>
                Fila {err.row}: {err.errors.join(', ')}
              </li>
            ))}
            {validationErrors.length > 25 && <li>Y{validationErrors.length - 25} más…</li>}
          </ul>
        </AlertDescription>
      </Alert>
    )}

    {serverError && validationErrors.length === 0 && (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{serverError}</AlertDescription>
      </Alert>
    )}

    {preview && validationErrors.length === 0 && (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold text-brand-navy">Vista Previa de Datos (Primeras filas)</p>
        <div className="overflow-x-auto rounded-lg border border-border-subtle">
          <table className="w-full text-xs">
            <thead className="bg-bg-surface-raised">
              <tr>
                {preview.headers.slice(0, 5).map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold uppercase text-text-subtle tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {preview.rows.slice(0, 5).map((row) => (
                <tr key={preview.headers.map((header) => String(row[header] ?? '')).join('|')}>
                  {preview.headers.slice(0, 5).map((h) => (
                    <td key={h} className="px-3 py-2 text-text-muted">
                      {String(row[h] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-text-subtle flex items-center gap-1">
          <Pencil className="size-3" />
          Total de propiedades detectadas: {preview.rowCount}
        </p>
      </div>
    )}
  </div>
);

interface ConfirmStepProps {
  masterProject: Pick<Project, 'id' | 'name'>;
  subproject: Pick<Project, 'id' | 'name'>;
  propertyCount: number;
  action: UploadAction;
  onActionChange: (action: UploadAction) => void;
  serverError: string | null;
}

const ConfirmStep = ({
  masterProject,
  subproject,
  propertyCount,
  action,
  onActionChange,
  serverError,
}: ConfirmStepProps) => (
  <div className="flex flex-col gap-5">
    <div className="flex flex-col items-center text-center gap-3">
      <span className="flex size-12 items-center justify-center rounded-full bg-brand-vivid text-text-inverse">
        <Building2 className="size-6" />
      </span>
      <h2 className="text-xl font-extrabold font-display text-brand-navy">Confirmar Carga de Propiedades</h2>
      <p className="text-sm text-text-subtle max-w-md">
        Has subido un archivo para procesamiento masivo. Revisa el resumen y selecciona el modo de actualización antes
        de continuar.
      </p>
    </div>

    <div className="grid grid-cols-3 gap-3 rounded-lg border border-border-subtle bg-bg-surface-raised p-4">
      <SummaryItem icon={<FolderKanban className="size-4" />} label="Proyecto" value={masterProject.name} />
      <SummaryItem icon={<Building2 className="size-4" />} label="Subproyecto" value={subproject.name} />
      <SummaryItem icon={<CloudUpload className="size-4" />} label="Propiedades a procesar" value={propertyCount} />
    </div>

    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-brand-navy">Modo de Operación</p>
      <RadioGroup
        value={action}
        onValueChange={(v) => onActionChange(v as UploadAction)}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <ModeOption
          value="replace"
          icon={<AlertTriangle className="size-4 text-error" />}
          title="Reemplazar todas las propiedades"
          description="Elimina todas las propiedades existentes del subproyecto y crea las nuevas."
          selected={action === 'replace'}
          variant="danger"
        />
        <ModeOption
          value="add"
          icon={<CheckCircle2 className="size-4 text-brand-vivid" />}
          title="Agregar o actualizar propiedades"
          description="Mantiene las propiedades existentes y agrega o actualiza según el ID. Opción recomendada."
          selected={action === 'add'}
        />
      </RadioGroup>
    </div>

    {serverError && (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{serverError}</AlertDescription>
      </Alert>
    )}
  </div>
);

interface SummaryItemProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}

const SummaryItem = ({ icon, label, value }: SummaryItemProps) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-text-subtle">{label}</span>
    <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-navy">
      {icon}
      {value}
    </span>
  </div>
);

interface ModeOptionProps {
  value: UploadAction;
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  variant?: 'danger' | 'default';
}

const ModeOption = ({ value, icon, title, description, selected, variant = 'default' }: ModeOptionProps) => (
  <label
    htmlFor={`mode-${value}`}
    className={cn(
      'flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition',
      selected && variant === 'danger' && 'border-error bg-error-light/30',
      selected && variant !== 'danger' && 'border-brand-vivid bg-brand-light/30',
      !selected && 'border-border-subtle bg-bg-surface hover:border-border-default'
    )}
  >
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold text-brand-navy">{title}</span>
      </div>
      <p className="text-xs text-text-subtle">{description}</p>
    </div>
    <RadioGroupItem id={`mode-${value}`} value={value} className="mt-0.5 shrink-0" />
  </label>
);

export default PropertyUploadDialog;
